import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Phone, PhoneOff, User, Mail, Search, Filter, Eye, EyeOff, CheckCircle, XCircle, Menu, X } from 'lucide-react';
import { supabase } from './lib/supabaseClient'

const AgencyManager = () => {
  const [activeTab, setActiveTab] = useState('modelos');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contasLoading, setContasLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchModelos = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('modelos')
          .select('*')
          .order('nome')
  
        if (error) console.error('Erro ao buscar modelos:', error)
        else setModelos(data || [])
      } catch (err) {
        console.error('Erro inesperado:', err)
      } finally {
        setLoading(false);
      }
    }
    fetchModelos()
  }, [])

  const [contas, setContas] = useState([]);

  useEffect(() => {
    const fetchContas = async () => {
      setContasLoading(true);
      try {
        const { data, error } = await supabase
          .from('contas')
          .select('*')
          .order('nomeUsuario')
  
        if (error) console.error('Erro ao buscar contas:', error)
        else setContas(data || [])
      } catch (err) {
        console.error('Erro inesperado:', err)
      } finally {
        setContasLoading(false);
      }
    }
  
    fetchContas()
  }, [])
  
  const [editingModelo, setEditingModelo] = useState(null);
  const [editingConta, setEditingConta] = useState(null);
  const [showAddModelo, setShowAddModelo] = useState(false);
  const [showAddConta, setShowAddConta] = useState(false);
  
  const [novoModelo, setNovoModelo] = useState({ nome: '', numero: '', contatado: false });
  const [novaConta, setNovaConta] = useState({ nomeUsuario: '', email: '', status: 'Ativa' });

  const statusOptions = ['Ativa', 'Aquecida', 'Fria', 'Banida'];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ativa':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Banida':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Aquecida':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Fria':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Ativa':
        return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'Banida':
        return <XCircle className="w-3 h-3 mr-1" />;
      case 'Aquecida':
        return <Eye className="w-3 h-3 mr-1" />;
      case 'Fria':
        return <EyeOff className="w-3 h-3 mr-1" />;
      default:
        return <User className="w-3 h-3 mr-1" />;
    }
  };

  const adicionarModelo = async () => {
    if (novoModelo.nome && novoModelo.numero) {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('modelos')
          .insert([novoModelo])
          .select() // Return the inserted data
        
        if (error) {
          console.error('Erro ao salvar:', error);
          return;
        }
        
        setNovoModelo({ nome: '', numero: '', contatado: false });
        setShowAddModelo(false);
        // Use the returned data instead of fetching again
        setModelos(prev => [...prev, ...data]);
      } catch (err) {
        console.error('Erro inesperado:', err);
      } finally {
        setLoading(false);
      }
    }
  }

  const adicionarConta = async () => {
    if (novaConta.nomeUsuario && novaConta.email) {
      setContasLoading(true);
      try {
        const { data, error } = await supabase
          .from('contas')
          .insert([novaConta])
          .select() // Return the inserted data
          
        if (error) {
          console.error('Erro ao salvar conta:', error);
          return;
        }
        
        setNovaConta({ nomeUsuario: '', email: '', status: 'Ativa' });
        setShowAddConta(false);
        
        // Use the returned data instead of fetching again
        setContas(prev => [...prev, ...data]);
      } catch (err) {
        console.error('Erro inesperado:', err);
      } finally {
        setContasLoading(false);
      }
    }
  };

  const editarModelo = (modelo) => {
    setEditingModelo(modelo);
  };

  const editarConta = (conta) => {
    setEditingConta(conta);
  };

  const salvarModelo = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('modelos')
        .update({ nome: editingModelo.nome, numero: editingModelo.numero })
        .eq('id', editingModelo.id)
        .select() // Return the updated data
    
      if (error) {
        console.error('Erro ao atualizar:', error);
        return;
      }
    
      // Update just the changed model in the state
      setModelos(prev => prev.map(m => m.id === editingModelo.id ? data[0] : m));
      setEditingModelo(null);
    } catch (err) {
      console.error('Erro inesperado:', err);
    } finally {
      setLoading(false);
    }
  }

  const salvarConta = async () => {
    setContasLoading(true);
    try {
      const { data, error } = await supabase
        .from('contas')
        .update({
          nomeUsuario: editingConta.nomeUsuario,
          email: editingConta.email,
          status: editingConta.status
        })
        .eq('id', editingConta.id)
        .select() // Return the updated data
      
      if (error) {
        console.error('Erro ao atualizar conta:', error);
        return;
      }
    
      // Update just the changed account in the state
      setContas(prev => prev.map(c => c.id === editingConta.id ? data[0] : c));
      setEditingConta(null);
    } catch (err) {
      console.error('Erro inesperado:', err);
    } finally {
      setContasLoading(false);
    }
  };

  const removerModelo = async (id) => {
    try {
      const { error } = await supabase
        .from('modelos')
        .delete()
        .eq('id', id)
      
      if (error) {
        console.error('Erro ao remover:', error);
        return;
      }
    
      // Update local state after successful deletion
      setModelos((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error('Erro inesperado:', err);
    }
  }
  
  const removerConta = async (id) => {
    setContasLoading(true);
    try {
      const { error } = await supabase
        .from('contas')
        .delete()
        .eq('id', id)
        
      if (error) {
        console.error('Erro ao remover conta:', error);
        return;
      }
    
      setContas((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Erro inesperado:', err);
    } finally {
      setContasLoading(false);
    }
  };

  const toggleContatoModelo = async (id) => {
    try {
      // Find the current model and get its contatado status
      const modelo = modelos.find(m => m.id === id);
      if (!modelo) return;
      
      const newContatadoStatus = !modelo.contatado;
      
      // Update the UI immediately for better UX
      setModelos(modelos.map(m => 
        m.id === id ? { ...m, contatado: newContatadoStatus } : m
      ));
      
      // Persist to Supabase
      const { error } = await supabase
        .from('modelos')
        .update({ contatado: newContatadoStatus })
        .eq('id', id);
      
      if (error) {
        console.error('Erro ao atualizar status de contato:', error);
        // Revert UI if there was an error
        setModelos(modelos.map(m => 
          m.id === id ? { ...m, contatado: modelo.contatado } : m
        ));
      }
    } catch (err) {
      console.error('Erro inesperado:', err);
    }
  };

  const toggleStatusConta = async (id) => {
    try {
      // First get the current conta
      const conta = contas.find(c => c.id === id);
      if (!conta) return;
      
      const currentIndex = statusOptions.indexOf(conta.status);
      const nextIndex = (currentIndex + 1) % statusOptions.length;
      const newStatus = statusOptions[nextIndex];
      
      const { error } = await supabase
        .from('contas')
        .update({ status: newStatus })
        .eq('id', id)
      
      if (error) return console.error('Erro ao atualizar status da conta:', error)
      
      setContas(contas.map(c => 
        c.id === id ? { ...c, status: newStatus } : c
      ));
    } catch (err) {
      console.error('Erro inesperado:', err)
    }
  };

  // Filtrar dados
  const filteredModelos = modelos.filter(modelo => {
    const matchesSearch = modelo.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         modelo.numero.includes(searchTerm);
    const matchesFilter = filterStatus === 'todos' || 
                         (filterStatus === 'contatado' && modelo.contatado) ||
                         (filterStatus === 'nao-contatado' && !modelo.contatado);
    return matchesSearch && matchesFilter;
  });

  const filteredContas = contas.filter(conta => {
    const matchesSearch = conta.nomeUsuario?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conta.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'todos' || conta.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const renderTabelaModelos = (dados) => {
    if (loading) {
      return (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900">Carregando modelos...</h3>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-pink-900 to-pink-800">
              <tr>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Número
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {dados.map((item, index) => (
                <tr key={item.id} className="hover:bg-gradient-to-r hover:from-pink-50 hover:to-rose-50 transition-all duration-200">
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {editingModelo && editingModelo.id === item.id ? (
                      <input
                        type="text"
                        value={editingModelo.nome}
                        onChange={(e) => setEditingModelo({...editingModelo, nome: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="text-sm font-semibold text-gray-900">{item.nome}</div>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {editingModelo && editingModelo.id === item.id ? (
                      <input
                        type="text"
                        value={editingModelo.numero}
                        onChange={(e) => setEditingModelo({...editingModelo, numero: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="text-sm text-gray-700 font-mono">{item.numero}</div>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleContatoModelo(item.id)}
                      className={`flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 hover:scale-105 ${
                        item.contatado
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200'
                          : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
                      }`}
                    >
                      {item.contatado ? (
                        <>
                          <Phone className="w-3 h-3 mr-1" />
                          <span className="hidden sm:inline">Contatado</span>
                          <span className="inline sm:hidden">Ok</span>
                        </>
                      ) : (
                        <>
                          <PhoneOff className="w-3 h-3 mr-1" />
                          <span className="hidden sm:inline">Não contatado</span>
                          <span className="inline sm:hidden">Não</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2 sm:space-x-3">
                      {editingModelo && editingModelo.id === item.id ? (
                        <>
                          <button
                            onClick={salvarModelo}
                            className="text-emerald-600 hover:text-emerald-800 font-semibold transition-colors duration-200"
                          >
                            Salvar
                          </button>
                          <button
                            onClick={() => setEditingModelo(null)}
                            className="text-gray-600 hover:text-gray-800 font-semibold transition-colors duration-200"
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => editarModelo(item)}
                            className="text-pink-600 hover:text-pink-800 transition-colors duration-200 hover:scale-110"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removerModelo(item.id)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200 hover:scale-110"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTabelaContas = (dados) => {
    if (contasLoading) {
      return (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900">Carregando contas...</h3>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-pink-900 to-pink-800">
              <tr>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Nome de Usuário
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-pink-50 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {dados.map((item, index) => (
                <tr key={item.id} className="hover:bg-gradient-to-r hover:from-pink-50 hover:to-rose-50 transition-all duration-200">
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {editingConta && editingConta.id === item.id ? (
                      <input
                        type="text"
                        value={editingConta.nomeUsuario}
                        onChange={(e) => setEditingConta({...editingConta, nomeUsuario: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="text-sm font-semibold text-gray-900">@{item.nomeUsuario}</div>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {editingConta && editingConta.id === item.id ? (
                      <input
                        type="email"
                        value={editingConta.email}
                        onChange={(e) => setEditingConta({...editingConta, email: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="text-sm text-gray-700">{item.email}</div>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {editingConta && editingConta.id === item.id ? (
                      <select
                        value={editingConta.status}
                        onChange={(e) => setEditingConta({...editingConta, status: e.target.value})}
                        className="px-2 sm:px-3 py-2 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      >
                        {statusOptions.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        onClick={() => toggleStatusConta(item.id)}
                        className={`flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 hover:scale-105 ${getStatusColor(item.status)}`}
                      >
                        {getStatusIcon(item.status)}
                        <span className="hidden sm:inline">{item.status}</span>
                        <span className="inline sm:hidden">{item.status.substring(0, 3)}</span>
                      </button>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2 sm:space-x-3">
                      {editingConta && editingConta.id === item.id ? (
                        <>
                          <button
                            onClick={salvarConta}
                            className="text-emerald-600 hover:text-emerald-800 font-semibold transition-colors duration-200"
                          >
                            Salvar
                          </button>
                          <button
                            onClick={() => setEditingConta(null)}
                            className="text-gray-600 hover:text-gray-800 font-semibold transition-colors duration-200"
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => editarConta(item)}
                            className="text-pink-600 hover:text-pink-800 transition-colors duration-200 hover:scale-110"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removerConta(item.id)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200 hover:scale-110"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFormularioAdicionar = (tipo) => {
    const isModelos = tipo === 'modelos';
    
    if (isModelos) {
      if (!showAddModelo) return null;
      
      return (
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 mb-8 border border-gray-100 transform transition-all duration-300 animate-in slide-in-from-top">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
            <User className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-pink-600" />
            Adicionar Modelo
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Nome
              </label>
              <input
                type="text"
                value={novoModelo.nome}
                onChange={(e) => setNovoModelo({...novoModelo, nome: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200"
                placeholder="Digite o nome"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Número
              </label>
              <input
                type="text"
                value={novoModelo.numero}
                onChange={(e) => setNovoModelo({...novoModelo, numero: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200"
                placeholder="(11) 99999-9999"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Status
              </label>
              <select
                value={novoModelo.contatado}
                onChange={(e) => setNovoModelo({...novoModelo, contatado: e.target.value === 'true'})}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200"
              >
                <option value={false}>Não contatado</option>
                <option value={true}>Contatado</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 mt-6 sm:mt-8">
            <button
              onClick={() => setShowAddModelo(false)}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={adicionarModelo}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-lg hover:from-pink-700 hover:to-pink-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Adicionar
            </button>
          </div>
        </div>
      );
    } else {
      if (!showAddConta) return null;
      
      return (
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 mb-8 border border-gray-100 transform transition-all duration-300 animate-in slide-in-from-top">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-pink-600" />
            Adicionar Conta
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Nome de Usuário
              </label>
              <input
                type="text"
                value={novaConta.nomeUsuario}
                onChange={(e) => setNovaConta({...novaConta, nomeUsuario: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200"
                placeholder="Digite o nome de usuário"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={novaConta.email}
                onChange={(e) => setNovaConta({...novaConta, email: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200"
                placeholder="email@exemplo.com"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Status
              </label>
              <select
                value={novaConta.status}
                onChange={(e) => setNovaConta({...novaConta, status: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200"
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 mt-6 sm:mt-8">
            <button
              onClick={() => setShowAddConta(false)}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={adicionarConta}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-lg hover:from-pink-700 hover:to-pink-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Adicionar
            </button>
          </div>
        </div>
      );
    }
  };

  const renderControls = () => {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-8 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent w-full sm:w-64"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent appearance-none bg-white w-full"
              >
                <option value="todos">Todos</option>
                {activeTab === 'modelos' ? (
                  <>
                    <option value="contatado">Contatado</option>
                    <option value="nao-contatado">Não contatado</option>
                  </>
                ) : (
                  statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))
                )}
              </select>
            </div>
          </div>
          <button
            onClick={() => activeTab === 'modelos' ? setShowAddModelo(true) : setShowAddConta(true)}
            className="flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-lg hover:from-pink-700 hover:to-pink-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            {activeTab === 'modelos' ? 'Adicionar Modelo' : 'Adicionar Conta'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <div className="flex md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
            <div className="flex items-center justify-center flex-1 md:justify-center">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-pink-600 to-pink-700 bg-clip-text text-transparent">
                Femme Agency Management
              </h1>
            </div>
            <div className="hidden sm:flex sm:justify-end sm:items-center">
              <div className="text-right">
                <p className="text-xs sm:text-sm text-gray-600">Total</p>
                <p className="text-lg sm:text-2xl font-bold text-pink-600">
                  {activeTab === 'modelos' ? modelos.length : contas.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-gray-200">
          <div className="px-4 py-3 space-y-2">
            <button
              onClick={() => {
                setActiveTab('modelos');
                setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center justify-between py-3 px-4 rounded-lg ${
                activeTab === 'modelos'
                  ? 'bg-pink-50 text-pink-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <User className="w-4 h-4 mr-3" />
                <span className="font-medium">Modelos</span>
              </div>
              <span className="bg-pink-100 text-pink-800 text-xs font-bold px-2 py-1 rounded-full">
                {modelos.length}
              </span>
            </button>
            <button
              onClick={() => {
                setActiveTab('contas');
                setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center justify-between py-3 px-4 rounded-lg ${
                activeTab === 'contas'
                  ? 'bg-pink-50 text-pink-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-3" />
                <span className="font-medium">Contas</span>
              </div>
              <span className="bg-pink-100 text-pink-800 text-xs font-bold px-2 py-1 rounded-full">
                {contas.length}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Desktop Navigation */}
      <nav className="hidden md:block bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('modelos')}
              className={`py-4 px-6 border-b-3 font-semibold text-sm transition-all duration-200 ${
                activeTab === 'modelos'
                  ? 'border-pink-600 text-pink-600 bg-pink-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>Modelos</span>
                <span className="bg-pink-100 text-pink-800 text-xs font-bold px-2 py-1 rounded-full">
                  {modelos.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('contas')}
              className={`py-4 px-6 border-b-3 font-semibold text-sm transition-all duration-200 ${
                activeTab === 'contas'
                  ? 'border-pink-600 text-pink-600 bg-pink-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>Contas</span>
                <span className="bg-pink-100 text-pink-800 text-xs font-bold px-2 py-1 rounded-full">
                  {contas.length}
                </span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Conteúdo */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {renderControls()}
        
        {activeTab === 'modelos' && (
          <div>
            {renderFormularioAdicionar('modelos')}
            {loading ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-900">Carregando modelos...</h3>
              </div>
            ) : filteredModelos.length > 0 ? (
              renderTabelaModelos(filteredModelos)
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center">
                <User className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum modelo encontrado</h3>
                <p className="text-gray-600">Tente ajustar os filtros ou adicionar um novo modelo.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contas' && (
          <div>
            {renderFormularioAdicionar('contas')}
            {contasLoading ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-900">Carregando contas...</h3>
              </div>
            ) : filteredContas.length > 0 ? (
              renderTabelaContas(filteredContas)
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center">
                <Mail className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma conta encontrada</h3>
                <p className="text-gray-600">Tente ajustar os filtros ou adicionar uma nova conta.</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AgencyManager;
